/**
 * SessionExpiryWarningDialog — WCAG 2.2.1 (Timing Adjustable)
 *
 * When the JWT is within 5 minutes of expiry, the hook first attempts a
 * silent token refresh.  If the refresh succeeds the dialog never appears —
 * the new token has a full lifetime and the user is unaffected.  Only when
 * the refresh fails (revoked token, no refresh token, network error) does the
 * warning dialog appear so the user can save work or sign in again.
 *
 * Design decisions:
 * - Polling every 15 s is lightweight and avoids a setTimeout drift across
 *   tab sleep/wake cycles.
 * - A `refreshingRef` prevents concurrent refresh attempts across poll ticks.
 * - Countdown is exposed as <strong aria-live="off"> — not "polite" to avoid
 *   reading every second; the dialog itself is a live alertdialog that fires
 *   once.
 * - "Stay signed in" dismisses the dialog without logging out.
 * - "Sign in again" calls performLogoutSync() then replaces the history
 *   entry to prevent the Back button returning to an authenticated view.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { performLogoutSync, refreshSession } from "@/lib/auth";

// ── JWT exp parser ────────────────────────────────────────────────────────────

/**
 * Safely extracts the `exp` Unix timestamp from a JWT without a library.
 * Returns null if the token is missing, malformed, or has no `exp` claim.
 */
function parseJwtExp(token: string): number | null {
  try {
    // Base64url → base64 → JSON
    const raw = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(raw));
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
}

function getStoredToken(): string | null {
  try {
    // Impersonation tabs store in sessionStorage; regular sessions in localStorage.
    return (
      sessionStorage.getItem("dotnetbot.jwt.session") ||
      localStorage.getItem("dotnetbot.jwt") ||
      null
    );
  } catch {
    return null;
  }
}

// ── Constants ─────────────────────────────────────────────────────────────────

/** Seconds before expiry at which the warning dialog appears (300 = 5 minutes). */
export const WARN_BEFORE_SECONDS = 300;

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useSessionExpiryWarning() {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  // Prevent concurrent refresh attempts across poll ticks.
  const refreshingRef = useRef(false);

  const getSecondsLeft = useCallback((): number => {
    const token = getStoredToken();
    if (!token) return Infinity;
    const exp = parseJwtExp(token);
    if (!exp) return Infinity;
    return exp - Math.floor(Date.now() / 1000);
  }, []);

  // Poll every 15 seconds to detect when the window enters the warning zone.
  useEffect(() => {
    function check() {
      const secs = getSecondsLeft();
      if (secs > 0 && secs <= WARN_BEFORE_SECONDS) {
        // ── Attempt silent refresh first ──────────────────────────────────
        // If the server issues a new token pair, the next poll will see a
        // longer expiry and the dialog will never appear.  If refresh fails
        // (revoked token, no refresh token, network error) we fall back to
        // the warning dialog so the user can save their work.
        if (!refreshingRef.current) {
          refreshingRef.current = true;
          refreshSession().then((ok) => {
            refreshingRef.current = false;
            if (!ok) {
              // Refresh failed — show the warning dialog with the latest reading.
              const currentSecs = getSecondsLeft();
              if (currentSecs > 0 && currentSecs <= WARN_BEFORE_SECONDS) {
                setSecondsLeft(currentSecs);
                setShowWarning(true);
              }
            }
            // If ok: new token stored; next poll will see the extended expiry.
          });
        }
      } else {
        // Expired or no token → hide (logout handled by the 401 interceptor).
        if (secs <= 0) setShowWarning(false);
      }
    }
    check();
    const id = setInterval(check, 15_000);
    return () => clearInterval(id);
  }, [getSecondsLeft]);

  // 1-second countdown while the warning is visible.
  useEffect(() => {
    if (!showWarning) return;
    const id = setInterval(() => {
      setSecondsLeft((s) => {
        const next = s - 1;
        if (next <= 0) setShowWarning(false);
        return Math.max(0, next);
      });
    }, 1_000);
    return () => clearInterval(id);
  }, [showWarning]);

  return {
    showWarning,
    secondsLeft,
    dismiss: () => setShowWarning(false),
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m > 0) return `${m} minute${m !== 1 ? "s" : ""} ${s} second${s !== 1 ? "s" : ""}`;
  return `${s} second${s !== 1 ? "s" : ""}`;
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * Mount once in the authenticated app shell (AppLayout). Renders nothing until
 * the session is within WARN_BEFORE_SECONDS of expiry.
 */
export function SessionExpiryWarningDialog() {
  const { showWarning, secondsLeft, dismiss } = useSessionExpiryWarning();

  function handleSignInAgain() {
    performLogoutSync();
    window.location.replace("/login");
  }

  // Nothing to show — not within the warning window yet.
  if (!showWarning) return null;

  return (
    <AlertDialog open={showWarning}>
      <AlertDialogContent
        // Radix AlertDialog already sets role="alertdialog" and aria-modal="true".
        aria-labelledby="session-expiry-title"
        aria-describedby="session-expiry-desc"
      >
        <AlertDialogHeader>
          <AlertDialogTitle id="session-expiry-title">
            Your session is about to expire
          </AlertDialogTitle>
          <AlertDialogDescription id="session-expiry-desc" className="space-y-1">
            <span>
              For your security, you will be signed out in{" "}
              {/* aria-live="off" because the whole dialog was already announced;
                  reading every second would be disruptive. */}
              <strong aria-live="off" aria-atomic="true">
                {formatCountdown(secondsLeft)}
              </strong>
              .
            </span>
            <span className="block text-sm">
              Save any unsaved work now. Click <em>Stay signed in</em> to dismiss
              this warning, or <em>Sign in again</em> to start a new session.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          {/* autoFocus on the safe action (stay) — mirrors the confirm-dialog pattern */}
          <AlertDialogCancel onClick={dismiss} autoFocus>
            Stay signed in
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleSignInAgain}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Sign in again
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
